import {
  Box,
  constants,
  Field,
  Skeleton,
  Stack,
  Text,
} from '@redbubble/design-system';
import { LoadingBoundary } from '../../LoadingBoundary';
import type {
  BasicConfiguratorOption,
  CommonAttributeConfiguratorProps,
} from './types';

type ReadonlyFieldSkeletonProps = {
  actionSlot: boolean;
};

const ReadonlyFieldSkeleton: React.FC<ReadonlyFieldSkeletonProps> = ({
  actionSlot,
}) => {
  return (
    <Box marginLeft="xs" marginRight="xs" marginBottom="l">
      <Stack spacing="xs">
        <Box display="flex" flexDirection="row" justifyContent="space-between">
          <Skeleton height={26} width={130} animated roundedCorners />

          {actionSlot && (
            <Skeleton height={26} width={150} animated roundedCorners />
          )}
        </Box>
        <Skeleton height={48} animated roundedCorners />
      </Stack>
    </Box>
  );
};

/**
 * This component represents an option in the configurator, with only one option available.
 * To keep the interface for all configurators consistent, we still accept an array of options
 * We extract the first item if present from the options array and render that
 */
export type ReadonlyFieldConfiguratorProps = {
  options: Array<BasicConfiguratorOption>;
  errors: Record<string, string>;
} & CommonAttributeConfiguratorProps;

export const ReadonlyFieldConfigurator: React.FC<
  ReadonlyFieldConfiguratorProps
> = ({ label, attributeName, currentValue, options, errors, actionSlot }) => {
  const option = options.length ? options[0] : undefined;

  return (
    <LoadingBoundary
      loadingComponent={<ReadonlyFieldSkeleton actionSlot={!!actionSlot} />}
    >
      <Field
        label={label}
        fluid
        type="button-toggle"
        data-testid="readonly-configurator"
        intent={errors[attributeName] && constants.ERROR}
        name={attributeName}
        value={currentValue}
        actionSlot={actionSlot}
      >
        {option && <Text>{option.defaultText}</Text>}
      </Field>
    </LoadingBoundary>
  );
};
